import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Button,
  Icon,
  Snackbar,
  Typography,
  makeStyles
} from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import { DesignedConfirmDialog } from 'components';
import {
  resendOrderEmailApi,
  unholdOrdersApi,
  generateOrdersInvoicePdfApi
} from 'api';


const useStyles = makeStyles({
  root: {
    borderTop: '1px solid #D0E9FF',
    marginTop: '-1px'
  },
  btnText: {
    color: '#434343',
    fontSize: '12px'
  },
  alertIcon: {
    '& .MuiAlert-icon .MuiSvgIcon-root.MuiSvgIcon-fontSizeInherit': {
      fill: '#FFF'
    },
  }
});

const refundAvailableStatuses = ['processing', 'complete', 'fraud', 'dispute'];
const shipmentAvailableStatuses = ['processing', 'closed', 'dispute'];

const OrderActions = ({order, setOpenShipment}) => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();

  const [openResendConfirmDialog, setOpenResendConfirmDialog] = useState(false);
  const [openUnholdDialog, setOpenUnholdDialog] = useState(false);
  const [openCopyNotification, setOpenCopyNotification] = useState(false);

  const {
    entity_id,
    status,
    items,
    extension_attributes: {dermpro_fraud_score},
    status_histories
  } = order;

  const {
    orderItemsDetails,
    loadingStoreOrderInvoicePdf
  } = useSelector((state: IReduxState) => state.store_orders);

  const isInvoiceAvailable = status === 'pending' || status_histories?.some((item) => item?.entity_name === 'invoice');
  const isAllQtyRefunded = items.filter((i) => i.qty_ordered === i.qty_refunded);
  const isRefundable = (refundAvailableStatuses.includes(status)
    || (status === 'closed' && order?.total_refunded < order.total_invoiced)
    || order?.payment?.method === 'modmed_payment')
    && (items.length !== isAllQtyRefunded?.length || order?.shipping_refunded !== order?.shipping_invoiced)
    && (!order?.payment.additional_information.includes('No Payment Information Required')
    || order?.extension_attributes.mp_reward.mp_reward_base_discount > 0
    || order?.extension_attributes.gift_card_value < 0
    || order?.extension_attributes.credit < 0);

  const isPhysicalItemsForShip = items.some((item) => item.is_virtual !== 1 &&
    item.qty_ordered !== item.qty_shipped &&
    item.qty_ordered !== item.qty_refunded
  );

  const isShippable = shipmentAvailableStatuses.includes(status) && isPhysicalItemsForShip;

  const handleOpenInvoice = () => {
    generateOrdersInvoicePdfApi({dispatch, orderIds: [entity_id]});
  };

  const copyEmail = () => {
    navigator.clipboard.writeText(order.customer_email);
    setOpenCopyNotification(true);
  };

  return (
    <div className={classes.root}>
      <div className="flex items-center justify-between px-7 py-3">
        <div className="flex">
          {isRefundable &&
          <Button
            variant="contained"
            className="mr-2 text-14 uppercase"
            color="primary"
            size="small"
            onClick={() => history.push({
              pathname: '/orders/refund',
              state: {
                order,
                orderItemsDetails: orderItemsDetails[entity_id]
              }
            })}
          >
            Refund
          </Button>
          }
          {isShippable &&
          <Button
            variant="contained"
            className="mr-2 text-14 uppercase"
            color="primary"
            size="small"
            onClick={() => setOpenShipment(true)}
          >
            Ship
          </Button>
          }
          {order.status === 'holded' &&
          <Button
            variant="contained"
            className="mr-2 text-14 uppercase"
            color="primary"
            size="small"
            onClick={() => setOpenUnholdDialog(true)}
          >
            Unhold
          </Button>
          }
          {dermpro_fraud_score &&
          <Typography
            className={`m-2 ${dermpro_fraud_score > 5 ? 'text-error' : ''}`}
            variant="body1"
          >
            Risk Score {dermpro_fraud_score}%
          </Typography>
          }
        </div>
        <div>
          <Button
            title="Copy email to clipboard"
            className={classes.btnText}
            color="primary"
            size="small"
            onClick={copyEmail}
          >
            <Icon className="mr-2">content_copy</Icon>
            <span>COPY EMAIL</span>
          </Button>
          <Button
            size="small"
            className={classes.btnText}
            onClick={() => setOpenResendConfirmDialog(true)}
          >
            <Icon className="mr-2">mail_outlined</Icon>
            <span>RESEND ORDER EMAIL</span>
          </Button>
          <Button
            size="small"
            className={classes.btnText}
            onClick={handleOpenInvoice}
            disabled={loadingStoreOrderInvoicePdf || !isInvoiceAvailable}
          >
            <Icon className="mr-2">print_outlined</Icon>
            <span>PRINT INVOICE</span>
          </Button>
        </div>
      </div>
      <DesignedConfirmDialog
        open={openResendConfirmDialog}
        setOpen={setOpenResendConfirmDialog}
        onConfirm={() => resendOrderEmailApi({dispatch, orderId: entity_id})}
      >
        Are you sure you want to send an order email to customer?
      </DesignedConfirmDialog>
      <DesignedConfirmDialog
        open={openUnholdDialog}
        setOpen={setOpenUnholdDialog}
        onConfirm={() => unholdOrdersApi({dispatch, orderIds: [order.entity_id]})}
      >
        {'Do you want UNHOLD selected order?'}
      </DesignedConfirmDialog>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={1000}
        onClose={() => setOpenCopyNotification(false)}
        open={openCopyNotification}
      >
        <MuiAlert elevation={6} variant="filled" className={classes.alertIcon} severity="info">
          Copied to clipboard
        </MuiAlert>
      </Snackbar>
    </div>
  );
};

export default OrderActions;
